import { useEffect } from 'react'
import { useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom'
import './App.css'
import Settings from './pages/Settings'
import AdminDashboard from './pages/AdminDashboard'
import Dashboard from './pages/Dashboard'
import DeviceDetails from './pages/admin/DeviceDetails'
import ConnectionStatusBadge from './components/ConnectionStatusBadge'
import FallAlarmBanner from './components/FallAlarmBanner'
import { IdentityBadge } from './components/Identity'
import OEMApp from './pages/oem/OEMApp'
import AccountSecurityPage from './pages/account/AccountSecurity'
import { canUseDispatcher, hasOEMRole, isOEMAdmin, useIdentity } from './services/identity'

function App() {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    document.body.style.height = '100%'
    document.documentElement.style.height = '100%'

    return () => {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
      document.body.style.height = ''
      document.documentElement.style.height = ''
    }
  }, [])

  useEffect(() => {
    const handleModalKeyboard = (event: KeyboardEvent) => {
      if (event.isComposing) return

      const modals = Array.from(document.querySelectorAll<HTMLElement>('[data-management-modal]'))
      const modal = modals[modals.length - 1]

      if (event.key === 'Escape') {
        const scope = modal || document
        const buttons = Array.from(scope.querySelectorAll<HTMLButtonElement>('button:not([disabled])'))
        const cancelButton = [...buttons].reverse().find((button) => {
          const label = button.textContent?.trim().toLowerCase() || ''
          return label === 'cancel' || label === 'close' || label === 'no'
        })
        if (cancelButton) {
          event.preventDefault()
          cancelButton.click()
        }
        return
      }

      if (!modal) return

      const buttons = Array.from(modal.querySelectorAll<HTMLButtonElement>('button:not([disabled])'))
      if (event.key !== 'Enter' || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLButtonElement) return

      const actionButton = modal.querySelector<HTMLButtonElement>('button[type="submit"]') || buttons[buttons.length - 1]
      if (actionButton) {
        event.preventDefault()
        actionButton.click()
      }
    }

    document.addEventListener('keydown', handleModalKeyboard)
    return () => document.removeEventListener('keydown', handleModalKeyboard)
  }, [])

  // An OEM person without customer networks gets only the separate OEM area. An OEM person whose
  // OEM administers customers gets the normal application for them (OEM-derived authority) and the
  // OEM area under /OEM. The server authorizes every request regardless.
  const { me } = useIdentity()
  if (isOEMAdmin(me) || (hasOEMRole(me) && location.pathname.startsWith('/OEM'))) return <OEMApp />

  // Determine active page from URL
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/' || location.pathname === ''
    return location.pathname.startsWith(path)
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-900 text-white">
      <FallAlarmBanner />
      <header className="sticky top-0 z-50 bg-gray-800 shadow">
        <div className="flex items-center py-6">
          <div className="w-64 flex justify-center shrink-0">
            <img src="/logo.png" alt="KAN-Consult" className="h-14 w-auto" />
          </div>
          <div className="flex items-center gap-10 px-4">
            <div>
              <h1 className="text-3xl font-bold">PTT Radio - Management Client</h1>
              <p className="text-gray-400">System administration and fleet management</p>
            </div>
            <nav className="flex gap-4">
              <button
                onClick={() => navigate('/')}
                className={`px-4 py-2 rounded font-medium ${
                  isActive('/')
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => navigate('/Admin/Dashboard')}
                className={`px-4 py-2 rounded font-medium ${
                  isActive('/Admin')
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                ⚙️ Admin
              </button>
              <button
                onClick={() => navigate('/Settings')}
                className={`px-4 py-2 rounded font-medium ${
                  isActive('/Settings')
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Settings
              </button>
              {hasOEMRole(me) && (
                <button
                  onClick={() => navigate('/OEM')}
                  className="px-4 py-2 rounded font-medium bg-gray-700 text-gray-300 hover:bg-gray-600"
                >
                  OEM
                </button>
              )}
              {canUseDispatcher(me) && (
                // A separate app on the same origin (not a client-side route): a full page load.
                <a
                  href="/dispatcher/"
                  title="Open the Dispatcher (control-room console)"
                  className="px-4 py-2 rounded font-medium bg-gray-700 text-gray-300 hover:bg-gray-600"
                >
                  📻 Dispatcher
                </a>
              )}
            </nav>
            <ConnectionStatusBadge />
            <IdentityBadge />
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-hidden">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/Account" element={<AccountSecurityPage />} />
          <Route path="/Admin/Network" element={<Navigate to="/Settings/customers" replace />} />
          <Route path="/Admin/assign" element={<Navigate to="/Settings/assign" replace />} />
          <Route path="/Admin/device/:radioId" element={<div className="h-full overflow-y-auto max-w-7xl mx-auto px-6 py-8"><DeviceDetails /></div>} />
          <Route path="/Admin/:section?" element={<AdminDashboard />} />
          <Route path="/Settings/:section?" element={<Settings />} />
          <Route path="/Settings/:section/:subsection" element={<Settings />} />
        </Routes>
      </main>
    </div>
  )
}

export default App

